import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, Plus, Trash2, Save, HelpCircle, X, Pencil } from "lucide-react";

const empty = {
    id: null,
    question: "",
    answer: "",
    order: 0,
};

export default function FAQManager() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [draft, setDraft] = useState(empty);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/faq");
            setItems(data);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const reset = () => setDraft(empty);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!draft.question.trim()) {
            setError("Pytanie jest wymagane.");
            return;
        }
        setSubmitting(true);
        setError("");
        try {
            const payload = {
                question: draft.question,
                answer: draft.answer,
                order: draft.order,
            };
            if (draft.id) {
                await api.put(`/faq/${draft.id}`, payload);
            } else {
                await api.post("/faq", payload);
            }
            reset();
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const onEdit = (item) => {
        setDraft({
            id: item.id,
            question: item.question || "",
            answer: item.answer || "",
            order: item.order ?? 0,
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const onDelete = async (id) => {
        if (!window.confirm("Usunąć ten wpis FAQ?")) return;
        try {
            await api.delete(`/faq/${id}`);
            if (draft.id === id) reset();
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    return (
        <div data-testid="admin-faq" className="grid lg:grid-cols-12 gap-8">
            <form
                onSubmit={onSubmit}
                data-testid="faq-form"
                className="panel-yellow p-6 lg:col-span-5 h-fit"
            >
                <div className="flex items-center justify-between mb-3">
                    <div className="label-eyebrow flex items-center gap-2">
                        <HelpCircle size={14} />
                        {draft.id ? "Edytuj wpis FAQ" : "Dodaj wpis FAQ"}
                    </div>
                    {draft.id && (
                        <button
                            type="button"
                            data-testid="faq-cancel-edit"
                            onClick={reset}
                            className="text-xs text-[color:var(--text-secondary)] hover:text-white inline-flex items-center gap-1"
                        >
                            <X size={12} /> Anuluj
                        </button>
                    )}
                </div>

                <Field label="Pytanie *">
                    <input
                        data-testid="faq-question-input"
                        value={draft.question}
                        onChange={(e) =>
                            setDraft((d) => ({ ...d, question: e.target.value }))
                        }
                        placeholder="Np. Jak zainstalować Project Ruda Śląska?"
                        required
                        maxLength={250}
                        className="input"
                    />
                </Field>

                <Field
                    label={`Odpowiedź · ${(draft.answer || "").length} znaków`}
                >
                    <textarea
                        data-testid="faq-answer-input"
                        value={draft.answer}
                        onChange={(e) =>
                            setDraft((d) => ({ ...d, answer: e.target.value }))
                        }
                        rows={8}
                        placeholder="Pełna odpowiedź. Możesz używać przejść do nowej linii."
                        className="input"
                    />
                </Field>

                <Field label="Kolejność (mniejsza = wyżej)">
                    <input
                        type="number"
                        data-testid="faq-order-input"
                        value={draft.order}
                        onChange={(e) =>
                            setDraft((d) => ({
                                ...d,
                                order: parseInt(e.target.value || "0", 10),
                            }))
                        }
                        className="input"
                    />
                </Field>

                {error && (
                    <div data-testid="faq-error" className="text-sm text-red-400 mb-3">
                        {error}
                    </div>
                )}

                <button
                    data-testid="faq-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full mt-2 disabled:opacity-60"
                >
                    {submitting ? (
                        <Loader2 className="animate-spin" size={14} />
                    ) : draft.id ? (
                        <Save size={14} />
                    ) : (
                        <Plus size={14} />
                    )}
                    {draft.id ? "Zapisz zmiany" : "Dodaj wpis"}
                </button>

                <style>{`
                    .input { width:100%; background: var(--bg-base); border:1px solid rgba(255,255,255,0.1); border-radius:6px; padding:8px 12px; color:white; font-size:14px; }
                    .input:focus { outline:none; border-color: var(--primary); }
                `}</style>
            </form>

            <div className="lg:col-span-7">
                <div className="label-eyebrow mb-4">
                    Wpisy FAQ ({items.length})
                </div>
                {loading ? (
                    <Loader2 className="animate-spin text-[color:var(--text-secondary)]" />
                ) : items.length === 0 ? (
                    <div className="panel-yellow p-8 text-center text-sm text-[color:var(--text-secondary)]">
                        <HelpCircle
                            size={24}
                            className="mx-auto mb-3 text-[color:var(--primary)]"
                        />
                        Brak wpisów FAQ. Dodaj pierwsze pytanie po lewej.
                    </div>
                ) : (
                    <ul className="space-y-3">
                        {items.map((it) => (
                            <li
                                key={it.id}
                                data-testid={`faq-row-${it.id}`}
                                className="panel-yellow p-4 flex items-start gap-3"
                            >
                                <span className="shrink-0 mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-md bg-[color:var(--bg-base)] border border-white/10 text-[10px] text-[color:var(--text-muted)]">
                                    #{it.order ?? 0}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="text-white font-medium leading-snug">
                                        {it.question}
                                    </div>
                                    {it.answer && (
                                        <p className="text-xs text-[color:var(--text-secondary)] mt-1 line-clamp-3 whitespace-pre-wrap">
                                            {it.answer}
                                        </p>
                                    )}
                                </div>
                                <button
                                    data-testid={`faq-edit-${it.id}`}
                                    onClick={() => onEdit(it)}
                                    className="text-xs uppercase tracking-wider text-[color:var(--text-secondary)] hover:text-white px-2 inline-flex items-center gap-1"
                                >
                                    <Pencil size={12} /> Edytuj
                                </button>
                                <button
                                    data-testid={`faq-delete-${it.id}`}
                                    onClick={() => onDelete(it.id)}
                                    className="text-red-400 hover:text-red-300 p-2"
                                    aria-label="Usuń"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block mb-3">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                {label}
            </span>
            {children}
        </label>
    );
}
